// Admin finance export (Task 24; spec §6.7): monthly totals per
// business/period, sourced from `host_statements` via the `admin-finance`
// edge function (host_statements RLS is owner-only, so the founder can only
// see cross-business totals through a service-role function). CSV export
// reuses `toCsv`/`downloadCsv` (Task 13's formula-injection-safe encoder) —
// the same 40-line encoder the bookings export uses, per plan.md's bundle
// budget.
//
// Review round 1 additions:
//   - item 2: two inline-SVG charts (fees / refunds per month, last 12).
//   - item 5: "Regenerate" on every row (not just rows with no PDF yet —
//     a host who disputes a number needs to force a recompute even when a
//     PDF already exists), plus a period picker that regenerates EVERY
//     eligible business for a period with zero rows at all (the previous
//     UI had no way to generate anything for a period nothing had ever
//     been generated for).
//   - minor: `monthLabel` imported from `lib/data/statements` instead of a
//     second copy; CSV money columns are raw signed 2-decimal numbers, not
//     comma-grouped display strings; the list is paginated ("Load more")
//     and CSV export pages through every row, not just the first page.
//
// Review round 2 additions:
//   - item 3: charts now built from EVERY page in the window (a dedicated
//     `fetchAllFinanceRows` query), not just the first PAGE_SIZE table rows
//     — a business list past the first page previously vanished from the
//     chart totals with no indication anything was missing.
//   - item 5: a from/to month-range picker (in addition to the auto-expand
//     below) plus an explicit success message after "Generate for all" —
//     generating a period outside the CURRENT window used to silently
//     succeed with no visible change, which read as "did nothing".
//     Generating now also auto-expands the window to include that period
//     if it falls outside it, so the result is visible without a second
//     manual step.
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '@/lib/auth/authContext';
import { callEdgeFunction } from '@/lib/api/edgeFunction';
import { toCsv, downloadCsv, type CsvColumn } from '@/lib/csv/toCsv';
import { formatNad } from '@/lib/money/format';
import { monthLabel } from '@/lib/data/statements';
import { MonthlyBarChart, type MonthlyChartPoint } from '@/lib/charts/MonthlyBarChart';
import { DownloadIcon } from '@/icons';
import { cancelThenInvalidate } from '@/lib/query/refresh';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/finance')({
  component: FinancePage,
});

interface FinanceRow {
  businessId: string;
  businessName: string;
  period: string;
  bookingsCount: number;
  feesPaidNad: number;
  /** Full in-app charges (the guest's whole payment, not a fee) — M7. */
  guestPaymentsNad?: number;
  feesRefundedNad: number;
  balancesRecordedNad: number;
  pdfPath: string | null;
}

interface FinanceListResponse {
  rows: FinanceRow[];
  from: string;
  to: string;
  limit: number;
  offset: number;
  hasMore: boolean;
}

interface GenerateResponse {
  ok: true;
  result: unknown;
  succeeded?: number;
  failed?: number;
  skipped?: number;
}

const PAGE_SIZE = 500;

/** Plain signed 2-decimal numeric strings for CSV money columns — a
 * spreadsheet formula/pivot needs a real number, not "N$ 1,234.56" (review
 * round 1, minor). Never comma-grouped. */
function rawMoney(n: number): string {
  return Number(n).toFixed(2);
}

const CSV_COLUMNS: CsvColumn<FinanceRow>[] = [
  { header: 'Business', value: (r) => r.businessName },
  { header: 'Period', value: (r) => r.period },
  { header: 'Bookings', value: (r) => r.bookingsCount },
  { header: 'Fees guests paid to NamibiaGo (N$)', value: (r) => rawMoney(r.feesPaidNad) },
  { header: 'Full payments guests made in-app (N$)', value: (r) => rawMoney(r.guestPaymentsNad ?? 0) },
  { header: 'Refunded to guests (N$)', value: (r) => rawMoney(r.feesRefundedNad) },
  { header: 'Balances recorded (N$)', value: (r) => rawMoney(r.balancesRecordedNad) },
];

function useFinanceRows(range: { from: string; to: string } | null) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-finance', 'list', PAGE_SIZE, range?.from, range?.to],
    queryFn: () =>
      callEdgeFunction<FinanceListResponse>(supabase, 'admin-finance', {
        action: 'list',
        limit: PAGE_SIZE,
        offset: 0,
        ...(range ?? {}),
      }),
  });
}

/** Fetches every page (not just the first) for `from`/`to` — used for both
 * the complete CSV export (review round 1, minor) AND the charts (review
 * round 2, item 3: the charts previously summed only the first PAGE_SIZE
 * table rows, so a business list past that page silently vanished from the
 * monthly totals). */
async function fetchAllFinanceRows(supabase: SupabaseClient, from: string, to: string): Promise<FinanceRow[]> {
  const rows: FinanceRow[] = [];
  let offset = 0;
  for (;;) {
    const page = await callEdgeFunction<FinanceListResponse>(supabase, 'admin-finance', {
      action: 'list',
      from,
      to,
      limit: PAGE_SIZE,
      offset,
    });
    rows.push(...page.rows);
    if (!page.hasMore) break;
    offset += PAGE_SIZE;
  }
  return rows;
}

/** All rows across the whole [from, to] window (every page), dedicated to
 * the charts — decoupled from the table's own (also paginated) display
 * query so neither one's row cap can hide data from the other. */
function useAllFinanceRowsForCharts(from: string | undefined, to: string | undefined) {
  const { supabase } = useAuth();
  return useQuery({
    queryKey: ['admin-finance', 'all-for-charts', from, to],
    queryFn: () => fetchAllFinanceRows(supabase, from as string, to as string),
    enabled: !!from && !!to,
  });
}

function useRegenerateStatement() {
  const { supabase } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { businessId?: string; period: string }) =>
      callEdgeFunction<GenerateResponse>(supabase, 'admin-finance', { action: 'generate', ...input }),
    onSuccess: () => {
      cancelThenInvalidate(queryClient, { queryKey: ['admin-finance'] });
    },
  });
}

/** Builds the x-axis (chronological) from a [from, to] window, summing
 * `rows` (across every business) per period — a period with zero activity
 * still appears as a zero-height bar, not a gap, so the window's shape is
 * always visible. */
function monthlySeries(rows: FinanceRow[], from: string, to: string, key: 'feesPaidNad' | 'feesRefundedNad'): MonthlyChartPoint[] {
  const totalsByPeriod = new Map<string, number>();
  for (const r of rows) {
    totalsByPeriod.set(r.period, (totalsByPeriod.get(r.period) ?? 0) + Number(r[key]));
  }
  const points: MonthlyChartPoint[] = [];
  const [fromY, fromM] = from.split('-').map(Number);
  const [toY, toM] = to.split('-').map(Number);
  let y = fromY ?? 0;
  let m = fromM ?? 1;
  for (;;) {
    const period = `${y}-${String(m).padStart(2, '0')}-01`;
    points.push({ period, value: totalsByPeriod.get(period) ?? 0 });
    if (y === toY && m === toM) break;
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    if (points.length > 36) break; // safety valve — a manually widened range should still terminate
  }
  return points;
}

/** `YYYY-MM-01` -> `YYYY-MM` for an `<input type="month">` value. */
function toMonthInputValue(period: string): string {
  return period.slice(0, 7);
}

function comparePeriods(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function FinancePage() {
  const { supabase } = useAuth();
  const [rangeOverride, setRangeOverride] = useState<{ from: string; to: string } | null>(null);
  const { data, isLoading, error } = useFinanceRows(rangeOverride);
  const chartsQuery = useAllFinanceRowsForCharts(data?.from, data?.to);
  const regenerate = useRegenerateStatement();
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [genPeriod, setGenPeriod] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');

  // Seed the picker inputs from the server's own default window once
  // loaded, so the picker always starts showing what's actually displayed.
  useEffect(() => {
    if (data && !rangeOverride) {
      setFromInput(toMonthInputValue(data.from));
      setToInput(toMonthInputValue(data.to));
    }
  }, [data, rangeOverride]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const chartRows = useMemo(() => chartsQuery.data ?? [], [chartsQuery.data]);
  const feesSeries = useMemo(
    () => (data ? monthlySeries(chartRows, data.from, data.to, 'feesPaidNad') : []),
    [chartRows, data],
  );
  const refundsSeries = useMemo(
    () => (data ? monthlySeries(chartRows, data.from, data.to, 'feesRefundedNad') : []),
    [chartRows, data],
  );

  async function handleExport() {
    if (!data) return;
    setExporting(true);
    try {
      const allRows = await fetchAllFinanceRows(supabase, data.from, data.to);
      const csv = toCsv(CSV_COLUMNS, allRows);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`namibiago-finance-${stamp}.csv`, csv);
    } finally {
      setExporting(false);
    }
  }

  function handleApplyRange(e: React.FormEvent) {
    e.preventDefault();
    if (!fromInput || !toInput) return;
    setRangeOverride({ from: `${fromInput}-01`, to: `${toInput}-01` });
  }

  /** After ANY successful generate, if `period` falls outside what's
   * currently shown, widen the window to include it — otherwise a
   * successful generate for, say, a year-old month reads as "did
   * nothing" (review round 2, item 5). */
  function expandRangeToInclude(period: string) {
    const currentFrom = data?.from ?? period;
    const currentTo = data?.to ?? period;
    if (comparePeriods(period, currentFrom) < 0 || comparePeriods(period, currentTo) > 0) {
      const newFrom = comparePeriods(period, currentFrom) < 0 ? period : currentFrom;
      const newTo = comparePeriods(period, currentTo) > 0 ? period : currentTo;
      setRangeOverride({ from: newFrom, to: newTo });
      return true;
    }
    return false;
  }

  function handleRegenerate(row: FinanceRow) {
    setRegenerateError(null);
    setSuccessMessage(null);
    regenerate.mutate(
      { businessId: row.businessId, period: row.period },
      {
        onSuccess: () => setSuccessMessage(`Statement regenerated for ${row.businessName}, ${monthLabel(row.period)}.`),
        onError: (err) => setRegenerateError(err instanceof Error ? err.message : 'Could not regenerate.'),
      },
    );
  }

  function handleGenerateForAll(e: React.FormEvent) {
    e.preventDefault();
    if (!genPeriod) return;
    setRegenerateError(null);
    setSuccessMessage(null);
    setGenBusy(true);
    const period = `${genPeriod}-01`;
    regenerate.mutate(
      { period },
      {
        onSuccess: (res) => {
          const expanded = expandRangeToInclude(period);
          const counts = typeof res.succeeded === 'number' ? ` (${res.succeeded} succeeded, ${res.failed ?? 0} failed, ${res.skipped ?? 0} skipped)` : '';
          setSuccessMessage(
            `Statement generation for ${monthLabel(period)} complete${counts}.${expanded ? ' The window below has been expanded to show it.' : ''}`,
          );
        },
        onError: (err) => setRegenerateError(err instanceof Error ? err.message : 'Could not generate.'),
        onSettled: () => setGenBusy(false),
      },
    );
  }

  return (
    <section className="flex flex-col gap-lg">
      <div className="flex flex-wrap items-center justify-between gap-md">
        <div>
          <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Finance export</h1>
          <p className="mt-sm text-body text-ink-2">
            Monthly host-statement totals across every business — fees guests paid to NamibiaGo and the balances
            hosts recorded. No mobile/host money ever passes through this table.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={rows.length === 0 || exporting}
          className="inline-flex items-center gap-xs rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
        >
          <DownloadIcon className="h-4 w-4" aria-hidden="true" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <form onSubmit={handleApplyRange} className="flex flex-wrap items-end gap-sm rounded-card border border-border bg-surface p-md shadow-card">
        <label className="flex flex-col gap-xs text-caption text-ink-2">
          From
          <input
            type="month"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            aria-label="Window start month"
          />
        </label>
        <label className="flex flex-col gap-xs text-caption text-ink-2">
          To
          <input
            type="month"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            aria-label="Window end month"
          />
        </label>
        <button type="submit" className="rounded-button border border-border bg-surface px-md py-xs text-body font-semibold text-ink">
          Apply
        </button>
      </form>

      <div className="grid grid-cols-1 gap-lg rounded-card border border-border bg-surface p-lg shadow-card sm:grid-cols-2">
        <MonthlyBarChart title="Fees guests paid to NamibiaGo, per month" points={feesSeries} colorVar="var(--canyon)" formatValue={formatNad} />
        <MonthlyBarChart title="Refunded to guests, per month" points={refundsSeries} colorVar="var(--desert)" formatValue={formatNad} />
      </div>

      <form onSubmit={handleGenerateForAll} className="flex flex-wrap items-end gap-sm rounded-card border border-border bg-surface p-md shadow-card">
        <label className="flex flex-col gap-xs text-caption text-ink-2">
          Generate a period for every eligible business
          <input
            type="month"
            value={genPeriod}
            onChange={(e) => setGenPeriod(e.target.value)}
            required
            className="rounded-input border border-border bg-surface px-sm py-xs text-body text-ink"
            aria-label="Period to generate"
          />
        </label>
        <button
          type="submit"
          disabled={!genPeriod || genBusy}
          className="rounded-button border border-border bg-surface px-md py-xs text-body font-semibold text-ink disabled:opacity-50"
        >
          {genBusy ? 'Generating…' : 'Generate for all businesses'}
        </button>
      </form>

      {isLoading ? <PageLoading /> : null}
      {error ? <p className="text-body text-danger">Couldn't load finance data: {(error as Error).message}</p> : null}
      {regenerateError ? <p className="text-body text-danger">{regenerateError}</p> : null}
      {successMessage ? <p className="text-body text-success">{successMessage}</p> : null}

      {!isLoading && !error && rows.length === 0 ? (
        <p className="rounded-card border border-border bg-surface p-lg text-body text-ink-2">
          No statements for this window.
        </p>
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto rounded-card border border-border bg-surface shadow-card">
          <table className="w-full min-w-[880px] text-body text-ink">
            <thead>
              <tr className="border-b border-hair text-left text-caption text-ink-2">
                <th className="px-md py-sm font-semibold">Business</th>
                <th className="px-md py-sm font-semibold">Period</th>
                <th className="px-md py-sm font-semibold">Bookings</th>
                <th className="px-md py-sm font-semibold">Fees guests paid to NamibiaGo</th>
                <th className="px-md py-sm font-semibold">Refunded to guests</th>
                <th className="px-md py-sm font-semibold">Balances recorded</th>
                <th className="px-md py-sm font-semibold">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.businessId}-${r.period}`} className="border-b border-hair last:border-0 hover:bg-surface-2">
                  <td className="px-md py-sm">{r.businessName}</td>
                  <td className="px-md py-sm">{monthLabel(r.period)}</td>
                  <td className="px-md py-sm">{r.bookingsCount}</td>
                  <td className="px-md py-sm">{formatNad(r.feesPaidNad)}</td>
                  <td className="px-md py-sm">{formatNad(r.feesRefundedNad)}</td>
                  <td className="px-md py-sm">{formatNad(r.balancesRecordedNad)}</td>
                  <td className="px-md py-sm">
                    <div className="flex items-center gap-sm">
                      {r.pdfPath ? <span className="text-caption text-success">generated</span> : <span className="text-caption text-warning">no PDF</span>}
                      <button
                        type="button"
                        onClick={() => handleRegenerate(r)}
                        disabled={regenerate.isPending}
                        className="text-caption font-semibold text-canyon underline disabled:opacity-50"
                      >
                        Regenerate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data?.hasMore ? (
            <p className="border-t border-hair px-md py-sm text-caption text-ink-2">
              Showing the first {PAGE_SIZE} rows — narrow the date range to see the rest (CSV export and the charts
              always cover the whole window).
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
